/**
 * What the configuration holds, in three hairline blocks: account rules (with the "−/+" steppers), reward information
 * and account details. The reward share stepper changes the real option (and the price); the target, drawdown, cycle
 * and trading-day steppers move to the neighbouring challenge type of the group, as the buyer would expect.
 * Compact by default (owner, 2026-10-30): the seven rules that decide a purchase show; the rest folds under "All
 * conditions" and stays on the full rules page.
 * Micro-scalping is never shown here (ADR-0032: only on the full rules page).
 */
import type { Phase, ProgramRules } from "@prop/contracts";
import { cn } from "@prop/ui";
import { Minus, Plus } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useConfigT, useRiskT } from "../../../i18n";
import { fmtQty, fmtUsd } from "../../../lib/fmt";
import { dayBar } from "../../../lib/rules";
import { axisOf, type Metric, neighbour } from "./model";
import { Collapse, Swap } from "./motion";
import type { Config } from "./useConfigurator";

type Amount = { pct: string } | { usd: string } | undefined;
/** Dollars of a rule amount at the account size, as a display value: $5,000. */
const dollars = (a: Amount, size: number): string | null => (!a ? null : "usd" in a ? fmtUsd(a.usd) : `$${fmtQty((size * Number(a.pct)) / 100, 2)}`);
const pctText = (a: Amount, size: number) => (!a ? "" : "pct" in a ? `${a.pct} %` : `${fmtQty((Number(a.usd) / size) * 100, 2)} %`);
const city = (tz: string) => (tz.split("/").at(-1) ?? tz).replace(/_/g, " ");

function Stepper({ label, onLess, onMore }: { label: string; onLess: (() => void) | null; onMore: (() => void) | null }) {
  const t = useConfigT();
  const btn = "inline-flex size-8 items-center justify-center rounded-full border border-border transition-colors duration-200 enabled:hover:border-foreground/50 enabled:hover:bg-muted disabled:opacity-30";
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button type="button" className={btn} disabled={!onLess} onClick={onLess ?? undefined} aria-label={t("rules.less", { k: label })}>
        <Minus className="size-3.5" aria-hidden />
      </button>
      <button type="button" className={btn} disabled={!onMore} onClick={onMore ?? undefined} aria-label={t("rules.more", { k: label })}>
        <Plus className="size-3.5" aria-hidden />
      </button>
    </span>
  );
}

/** One rule row: label (with a quiet note under it), the value on the right, an optional stepper after it. */
function Line({ k, v, note, step }: { k: string; v: ReactNode; note?: string | null; step?: ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3 border-b border-border py-1 last:border-0">
      <dt className="min-w-0 text-sm text-muted-foreground">
        {k}
        {note && <span className="flex text-xs">{note}</span>}
      </dt>
      <dd className="ml-auto flex items-center gap-3 text-right text-sm font-semibold tabular-nums">
        {typeof v === "string" ? <Swap value={v} /> : <span>{v}</span>}
        {step}
      </dd>
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-1">
      <h3 className="text-base font-semibold">{title}</h3>
      <dl>{children}</dl>
    </section>
  );
}

export function RulesPanel({ c }: { c: Config }) {
  const t = useConfigT();
  const r = c.rules;
  if (!r || !c.family) return null;
  const size = Number(r.accountSize);
  const evals = r.phases.filter((p) => p.kind === "evaluation");
  const first = r.phases[0] as Phase;
  const step = (m: Metric, label: string) => {
    const f = c.family;
    if (!f) return null;
    const less = neighbour(c.group, f, c.size, m, -1);
    const more = neighbour(c.group, f, c.size, m, 1);
    if (!less && !more) return null;
    return <Stepper label={label} onLess={less ? () => c.setFamily(less) : null} onMore={more ? () => c.setFamily(more) : null} />;
  };
  const split = axisOf(c.program, "split");
  const splitAt = split ? split.values.findIndex((v) => v.value === c.choices.split) : -1;
  const goSplit = (d: -1 | 1) => {
    const next = split?.values[splitAt + d];
    return next ? () => c.setPref("split", next.value) : null;
  };
  const targets = r.phases.filter((p) => p.profitTarget);
  const targetSum = evals.reduce((s, p) => s + (p.profitTarget ? ("pct" in p.profitTarget ? (size * Number(p.profitTarget.pct)) / 100 : Number(p.profitTarget.usd)) : 0), 0);
  const perf = r.payouts?.performanceReward;
  const tradeLoss = r.phases.find((p) => p.kind === "funded")?.maxTradeLoss;
  return (
    <div className="flex min-w-0 flex-col gap-4">
    <div className="grid min-w-0 gap-x-8 gap-y-5 lg:grid-cols-2">
      <Block title={t("rules.title")}>
        {r.payouts && (
          <Line
            k={t("rules.split")}
            v={`${r.payouts.profitSplitPct} %`}
            step={split ? <Stepper label={t("rules.split")} onLess={goSplit(-1)} onMore={goSplit(1)} /> : <Stepper label={t("rules.split")} onLess={null} onMore={null} />}
          />
        )}
        <Line
          k={t("rules.target")}
          v={targets.length ? targets.map((p) => pctText(p.profitTarget, size)).join(" · ") : t("rules.none")}
          note={targets.map((p) => dollars(p.profitTarget, size)).join(" · ") || null}
          step={step("target", t("rules.target"))}
        />
        <Line k={t("rules.daily")} v={first.dailyLoss ? pctText(first.dailyLoss.amount, size) : t("rules.none")} note={dollars(first.dailyLoss?.amount, size)} step={step("daily", t("rules.daily"))} />
        <Line k={t("rules.max")} v={pctText(first.maxLoss.amount, size)} note={dollars(first.maxLoss.amount, size)} step={step("max", t("rules.max"))} />
      </Block>
      <RewardBlock c={c} r={r} step={step} part="main" />
    </div>
    <AllConditions>
      <div className="grid min-w-0 gap-x-8 gap-y-5 lg:grid-cols-2">
        <Block title={t("rules.extra")}>
          {perf && targetSum > 0 && <Line k={t("rules.reward")} v={`$${fmtQty((targetSum * Number(perf.pct)) / 100, 2)}`} note={t("rules.rewardNote", { g: perf.minGrowthPct })} />}
          {tradeLoss && <Line k={t("rules.tradeLoss")} v={pctText(tradeLoss, size)} note={`${dollars(tradeLoss, size)} · ${t("rules.tradeLossNote")}`} />}
          <RewardBlock c={c} r={r} step={step} part="extra" />
        </Block>
        <DetailsBlock c={c} r={r} />
      </div>
    </AllConditions>
    </div>
  );
}

/** The fold: a quiet full-width toggle; what it holds opens with a height glide. */
function AllConditions({ children }: { children: ReactNode }) {
  const t = useConfigT();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-border text-sm font-medium transition-colors duration-200 hover:border-foreground/50 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? t("rules.allHide") : t("rules.all")}
        <ChevronDown className={cn("size-4 transition-transform duration-300", open && "rotate-180")} aria-hidden />
      </button>
      <Collapse open={open}>
        <div className="pt-5">{children}</div>
      </Collapse>
    </div>
  );
}

/** The reward rules: the three that decide a purchase (`main`), or the rest for the fold (`extra`, no title of its own). */
function RewardBlock({ c, r, step, part }: { c: Config; r: ProgramRules; step: (m: Metric, label: string) => ReactNode; part: "main" | "extra" }) {
  const t = useConfigT();
  const p = r.payouts;
  if (!p) return null;
  const pd = p.profitableDays;
  const evalCons = r.phases.find((x) => x.consistency)?.consistency;
  const firstCap = p.caps?.amounts[0] ?? p.capPerRequest;
  const fut = c.product === "futures";
  const waitDays = Math.max(p.firstPayoutAfterDays, pd?.days ?? 0);
  if (part === "main")
    return (
      <Block title={t("reward.title")}>
        <Line k={t("reward.cycle")} v={t(`reward.cycles.${p.cycle}`)} step={step("cycle", t("reward.cycle"))} />
        <Line
          k={t("reward.consistency")}
          v={p.consistencyPct ? t("reward.bestDay", { v: p.consistencyPct }) : t("rules.none")}
          note={evalCons ? t("reward.evalConsistency", { v: evalCons.maxPct }) : null}
        />
        <Line k={t("reward.days")} v={pd ? String(pd.days) : t("rules.none")} note={pd ? t("reward.daysNote", { v: dayBar(pd) }) : null} step={step("days", t("reward.days"))} />
      </Block>
    );
  return (
    <>
      {fut && <Line k={t("reward.rewardsIn")} v={waitDays > 0 ? t("reward.rewardsInDays", { n: waitDays }) : t("reward.onDemand")} />}
      {p.refundFeePct && p.refundFeeOnPayout && <Line k={t("reward.feeRefund")} v={t("reward.feeRefundCell", { pct: p.refundFeePct, n: p.refundFeeOnPayout })} />}
      {firstCap && <Line k={t("reward.cap")} v={fmtUsd(firstCap)} />}
      {p.minTrades && <Line k={t("reward.minTrades")} v={String(p.minTrades)} />}
      {p.bestTradePct && <Line k={t("reward.bestTrade")} v={t("reward.bestTradeCell", { v: p.bestTradePct })} />}
      {p.maxPayouts === 1 && <Line k={t("reward.payouts")} v={t("reward.once")} />}
      {r.scaling && (
        <Line
          k={t("reward.scaling")}
          v={t("reward.scalingCell", { step: r.scaling.stepPct, months: r.scaling.everyMonths })}
          note={t("reward.scalingNote", { max: fmtUsd(r.scaling.maxSizeUsd) })}
        />
      )}
    </>
  );
}

function DetailsBlock({ c, r }: { c: Config; r: ProgramRules }) {
  const t = useConfigT();
  const tr = useRiskT();
  const phases = r.phases;
  const first = phases[0] as Phase;
  const win = phases.find((p) => p.timeWindow)?.timeWindow;
  const days = phases.find((p) => p.maxDurationDays)?.maxDurationDays;
  const maxDays = phases.find((p) => p.maxTradingDays)?.maxTradingDays;
  const contracts = phases.find((p) => p.positionLimits?.maxContracts)?.positionLimits?.maxContracts;
  const flat = phases.find((p) => p.timeRules?.flatByLocalTime)?.timeRules?.flatByLocalTime;
  const b = r.dayBoundary;
  const tz = "session" in b ? "America/Chicago" : b.timezone;
  const dayEnd = "session" in b ? "17:00" : b.time;
  // News and weekend as the program has them (ADR-0043: FX / CFD funded phases close for the weekend, ±2 min of news).
  const newsMin = phases.find((p) => p.timeRules?.newsWindowMin)?.timeRules?.newsWindowMin;
  const weekendClosed = phases.some((p) => p.timeRules?.weekend === "auto_close");
  const count = c.program?.instrumentCount;
  const fee = c.price?.activationFeeUsd;
  const rows: Array<[string, ReactNode, string?]> = [
    [t("details.time"), win ? t("details.window", { h: win.minutes / 60 }) : days ? t("details.days", { n: days }) : t("details.unlimited")],
    ...(maxDays ? [[t("details.tradingDays"), String(maxDays)] as [string, string]] : []),
    [t("details.leverage"), `1:${fmtQty(r.leverage.default, 0)}`],
    ...(count ? [[t("details.instruments"), String(count)] as [string, string]] : []),
    [t("details.drawdownType"), tr(`programs.modes.${first.maxLoss.mode}`)],
    [t("details.dayEnds"), `${dayEnd} ${city(tz)}`],
    ...(contracts ? [[t("details.contracts"), t("details.contractsCell", { n: contracts })] as [string, string]] : []),
    ...(flat ? [[t("details.flat"), t("details.flatCell", { time: flat, city: city(tz) })] as [string, string]] : []),
    [t("details.news"), newsMin ? t("details.newsCell", { n: newsMin }) : t("details.allowed")],
    ...(weekendClosed ? [[t("details.weekend"), t("details.weekendCell")] as [string, string]] : !flat ? [[t("details.weekend"), t("details.allowed")] as [string, string]] : []),
    [t("details.coach"), t("details.included")],
    ...(fee ? [[t("details.activation"), t("details.activationCell", { fee: fmtUsd(fee) })] as [string, string]] : []),
  ];
  return (
    <section className="flex min-w-0 flex-col gap-1">
      <h3 className="text-base font-semibold">{t("details.title")}</h3>
      <dl className="grid">
        {rows.map(([k, v]) => (
          <div key={k} className="flex min-h-10 items-center justify-between gap-3 border-b border-border py-1">
            <dt className="text-sm text-muted-foreground">{k}</dt>
            <dd className="text-right text-sm font-semibold tabular-nums">{typeof v === "string" ? <Swap value={v} /> : v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
